import { useCallback } from "react"
import { useAppDispatch, useAppStore } from "hooks/redux"
import { Creators as DataCreators } from "model/data"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { isUuid } from "helpers/types/uuid"
import { log } from "helpers/logger"
import { useCreateLayout, LAYOUTS_RESOURCE, type SaveLayoutTier } from "resources/layouts"
import { useReseedInstance, DASHBOARD_INSTANCES_RESOURCE } from "resources/dashboard-instances"
import { createAndReseedLayout } from "../layouts/create-and-reseed-layout"
import { exportLayoutConfig, type DashboardPanelSlicesState } from "../layouts/export-layout-config"
import { instancesSlice } from "../store/instances-slice"
import type { GetArrangement } from "./use-workspace-sync"

export interface SaveAsNewLayout {
  // Resolves on success (toasts); rejects on failure so SaveAsNewDialog's catch can keep the dialog
  // open with its inline retry message instead of silently closing on a swallowed error. [APP-3309]
  saveAsNew: (name: string, tier: SaveLayoutTier) => Promise<void>
}

/**
 * "Save as new layout" (APP-2708): snapshots live dashboard state into a new layout, owned by the
 * caller ("user") or shared with the account ("account", APP-2690), and re-points the instance at it.
 *
 * @param dashboardId - The dashboard slot whose live state is exported; equals the instance to re-point.
 * @param getArrangement - Reads the live Dockview blob at call time (WYSIWYG); null when no engine is mounted.
 */
export function useSaveAsNewLayout(
  dashboardId: string,
  getArrangement: GetArrangement,
): SaveAsNewLayout {
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const createLayout = useCreateLayout()
  const reseedInstance = useReseedInstance()

  const saveAsNew = useCallback(
    async (name: string, tier: SaveLayoutTier) => {
      // Failures propagate for SaveAsNewDialog's inline retry, so nothing here also toasts.
      // Defense in depth: LayoutActionsMenu already gates on isUuid, but reject loudly here too
      // in case a caller skips it. [APP-3309]
      if (!isUuid(dashboardId)) {
        throw new Error(`saveAsNew requires a real (UUID) instance id, got "${dashboardId}"`)
      }
      // Export live state at call time (not a render-closure snapshot) so the copy is WYSIWYG.
      const state = store.getState() as unknown as DashboardPanelSlicesState
      const { config } = exportLayoutConfig(state, dashboardId, getArrangement())

      // Mints the layout and reseeds this instance to point at it — see create-and-reseed-layout.ts
      // for the orphaned-layout failure mode this also guards against.
      // Tier is bound in this closure, not createAndReseedLayout's shared signature, so useDuplicateDashboard's self-heal branch can't mint an account-tier layout. (APP-2690)
      const instance = await createAndReseedLayout({
        dashboardId,
        name,
        config,
        createLayout: (layoutName, layoutConfig) => createLayout(layoutName, layoutConfig, tier),
        reseedInstance,
      }).catch((error: unknown) => {
        // helpers/logger.ts reports to Sentry in production, unlike dev-logger's warn(). (APP-2690)
        log(new Error(`Save as new layout (tier: ${tier}) failed for dashboard ${dashboardId}`, { cause: error }))
        throw error
      })
      dispatch(instancesSlice.actions.upsertInstance({
        instanceId: instance.instanceId,
        name: instance.name,
        layoutId: instance.layoutId,
        layoutVersionSeeded: instance.layoutVersionSeeded,
        lastOpenedAt: instance.lastOpenedAt,
      }))

      // Reset both caches (mirrors useCreateDashboard): the instances list, since a later tab switch
      // re-hydrates from it and would reseed the old layout; and the layouts list. [APP-3266/3271]
      dispatch(DataCreators.resetCollections([DASHBOARD_INSTANCES_RESOURCE, LAYOUTS_RESOURCE]))

      dispatch(NotificationCreators.add(
        "success",
        null,
        tier === "account" ? `Shared "${name}" with the account.` : `Saved "${name}" as a new layout.`,
      ))
    },
    [store, dispatch, createLayout, reseedInstance, dashboardId, getArrangement],
  )

  return { saveAsNew }
}
